import { Injectable, OnDestroy } from '@angular/core';
import { ModalService } from '@app/modules/modals/services/modal.service';
import { NgbModalRef, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, merge, Observable, Subject, take, takeUntil, tap } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class SurfacePopoverStateService implements OnDestroy {
    private readonly unsubscribe$: Subject<void> = new Subject<void>();

    private readonly _isBackdropOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public readonly isBackdropOpen$: Observable<boolean> = this._isBackdropOpen$.asObservable();

    private readonly activePopovers: Set<NgbPopover> = new Set<NgbPopover>();
    private readonly popoverStickyParents: Map<NgbPopover, HTMLElement> = new Map<NgbPopover, HTMLElement>();
    private backdrop: NgbModalRef;

    constructor(
        private readonly modalService: ModalService
    ) {}

    /**
     * Registers a popover as opening and initializes the backdrop if needed.
     * @param popover - A unique popover instance
     * @param stickyParent - Optional sticky parent element
     */
    registerPopover(popover: NgbPopover, stickyParent?: HTMLElement): void {
        this.activePopovers.add(popover);
        if (stickyParent) {
            this.popoverStickyParents.set(popover, stickyParent);
        }
        this.initBackdrop();
    }

    /**
     * Registers a popover as closing and closes the backdrop if no other popovers are active.
     * @param popover - A unique popover instance
     */
    unregisterPopover(popover: NgbPopover): void {
        this.activePopovers.delete(popover);
        this.popoverStickyParents.delete(popover);

        if (this.activePopovers.size === 0) {
            this.closeBackdrop();
        }
    }

    /**
     * Initializes the backdrop if it is not already open.
     */
    private initBackdrop(): void {
        if (this.backdrop) {
            return;
        }

        this.backdrop = this.modalService.showWhiteModalBackdrop();
        this._isBackdropOpen$.next(true);

        merge(this.backdrop.closed, this.backdrop.dismissed).pipe(
            take(1),
            tap(() => {
                this.handleBackdropClosed();
            }),
            takeUntil(this.unsubscribe$)
        ).subscribe();
    }

    /**
     * Closes the backdrop manually.
     */
    private closeBackdrop(): void {
        this.backdrop?.close();
    }

    /**
     * Checks if there are other active popovers in the same sticky parent.
     * @param stickyParent - The sticky parent element to check
     * @returns true if there are other popovers in the same sticky parent
     */
    hasOtherPopoversInStickyParent(stickyParent: HTMLElement): boolean {
        if (!stickyParent) {
            return false;
        }

        for (const [popover, parent] of this.popoverStickyParents) {
            if (parent === stickyParent && this.activePopovers.has(popover)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Handles when the backdrop is closed (either manually or by user clicking away).
     * Clears all active popovers and updates state.
     */
    private handleBackdropClosed(): void {
        this.activePopovers.clear();
        this.popoverStickyParents.clear();
        this._isBackdropOpen$.next(false);
        this.backdrop = null;
    }

    ngOnDestroy(): void {
        this._isBackdropOpen$.next(false);
        this._isBackdropOpen$.complete();

        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
